import React, { useMemo } from 'react';
import { DropZone, createUsePuck } from '@puckeditor/core';
import { columnAllowedBlockTypes } from '../../../puck/pagePuckData';
import { useTemplateLockMode } from '../../../puck/templateLocks';
import styles from './ProposalSection.module.css';

const usePuckApi = createUsePuck();

/** allow=[] : la zone n'accepte aucun type → Puck désactive nativement la
 *  zone ET ses items pour le dnd (pas de preview, drop impossible). */
const BLOCK_ALL_TYPES = [];

/** Flex colonne : la zone est étirée par la grille (chaîne display:flex,
 *  cf. .columnZone dans le module CSS) et étire à son tour ses boxes —
 *  chaque box gère son propre alignement vertical (ProposalBoxAlign). */
function columnZoneStyle(style) {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    ...style,
  };
}

/**
 * Règle « 1 box par colonne » : colonne occupée → aucun drop possible.
 * Sélecteur booléen sur l'occupation de la zone — re-rendu uniquement quand
 * la colonne passe vide ↔ occupée, jamais pendant la frappe ou le drag.
 */
function EditorColumnDropZone({ sectionId, colId, style, ...dropZoneProps }) {
  const occupied = usePuckApi(
    (s) => (s.appState.data.zones?.[`${sectionId}:col-${colId}`]?.length ?? 0) > 0,
  );
  // Bloc verrouillé par le modèle : aucune box ne s'y ajoute sur le site.
  const enforcesLocks = useTemplateLockMode() === 'site';
  const locked = usePuckApi((s) => enforcesLocks && !!s.getItemById?.(sectionId)?.props?.locked);
  // Liste blanche recalculée seulement quand la config change (une composition
  // enregistrée, un verrou de collaboration) — jamais pendant la frappe.
  const config = usePuckApi((s) => s.config);
  const allowed = useMemo(() => columnAllowedBlockTypes(config), [config]);
  return (
    <DropZone
      {...dropZoneProps}
      style={style}
      zone={`col-${colId}`}
      allow={occupied || locked ? BLOCK_ALL_TYPES : allowed}
    />
  );
}

/**
 * DropZone d'une colonne de section.
 * - Éditeur : occupation lue dans le store Puck (usePuck — utilisable dans le
 *   canvas, cf. ProposalRichTextBubbleEditor) ; occupée = drop interdit.
 * - Viewer (<Render>, hors contexte <Puck>) : DropZone simple (div nu, notre
 *   style porte toute la mise en page).
 */
export default function SectionColumnDropZone({
  isEditing = false,
  sectionId,
  colId,
  style,
  className = '',
  ...dropZoneProps
}) {
  const zoneStyle = columnZoneStyle(style);
  const zoneClassName = `${styles.columnZone} ${className}`.trim();
  if (!isEditing) {
    return (
      <DropZone
        {...dropZoneProps}
        className={zoneClassName}
        style={zoneStyle}
        zone={`col-${colId}`}
      />
    );
  }
  return (
    <EditorColumnDropZone
      sectionId={sectionId}
      colId={colId}
      className={zoneClassName}
      style={zoneStyle}
      {...dropZoneProps}
    />
  );
}
